import React, { FC, useEffect, useState } from "react";
import { useSelector } from 'react-redux';
import TableTab from "../components/tabs/table-tab";
import { DEVICE_TIME_PATTERNS } from "../constants/menu-types";
import { useLocale } from "../contexts/localeContext";
import { TableContextProvider } from "../contexts/table/TableContextProvider";
import { TranslationKeys } from "../contexts/TranslationKeys";
import { Category } from "../models/Category";
import columns from '../models/ColumnDefinitions/DeviceTimePatternColumns';
import { Lookup } from "../models/Lookup";
import LookupTypes from "../models/LookupTypes";
import searchColumns from '../models/search/DeviceTimePatternSearch';
import SiteSearch from "../models/search/SiteSearch";
import { DeleteEntityResult } from "../models/services/DeleteEntityResult";
import { deleteDeviceTimePattern, getDeviceTimePatterns } from "../services/deviceTimePatternService";
import { getSiteNamesWithPagination } from "../services/siteService";
import TableContextStateMonitor from "../contexts/table/TableContextStateMonitor";
import { Filter } from "../models/Filter";
import { getDayNames } from "../utilities/DateUtils";
import { ListQuery } from "../models/ListQuery";

interface Props {
    setBusy: (isBusy: boolean) => void,
    categories: Category[],
    onFilterChange: (filters: Filter[]) => void,
    setCategories: (category: Category[]) => void
}

const DeviceTimePatternsTab: FC<Props> = ({ setBusy, categories, setCategories, onFilterChange }) => {
    const { translate } = useLocale();
    const title = translate.byKey(TranslationKeys.deviceTimePatterns);
    const [lookup, setLookup] = useState<Lookup[]>([]);
    const itemPath = '/access-control/hardware/device-time-pattern/';

    useEffect(() => {
        setLookup([{
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.days,
            values: getDayNames(translate)
        }]);
    }, [])

    const onDelete = (id: string): Promise<DeleteEntityResult> => {
        setBusy(true);
        return new Promise((resolve, reject) => {
            deleteDeviceTimePattern(id)
                .then(
                    deleteResult => {
                        resolve(deleteResult);
                        setCategories([{ ...categories[0], count: (categories[0]?.count ?? 1) - 1 }]);
                        setBusy(false);
                    },
                    deleteResult => {
                        reject(deleteResult);
                        setBusy(false);
                    })
        });
    }

    // user preference items
    const userLoginId = useSelector((state: any) => (state.userInfo.id))
    const initialiseUserPreferences = (setDetails: (userLoginId, DEVICE_TIME_PATTERNS) => void) => {
        setDetails(userLoginId, DEVICE_TIME_PATTERNS);
    }
    // <-- end context

    return (<TableContextProvider
        dataMethod={(listParameters?: ListQuery) => getDeviceTimePatterns(translate, listParameters)}
        initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
        tableDescriptor={columns}
        searchDescriptor={searchColumns}
        useFilter
        useSort
        usePagination
        useSearch
        useTable
        deleteMethod={onDelete}
        lookup={lookup}
        customDelete={{ fieldName: "isDefault", comparisonValue: false }}>
        <TableContextStateMonitor onFilterChange={onFilterChange} />
        <TableTab
            title={title}
            selectRows={false}
            rowPath={itemPath} 
            cyName="deviceTimePatterns"/>
    </TableContextProvider >
    );
};

export default DeviceTimePatternsTab;
